import React from 'react';

import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {exportCustomsAuditLog, fetchCustomsAuditLog} from 'api/customsAudit';
import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {AuditReference} from 'components/common/AuditLog/Reference';
import {EventId, PageContent, Toolbar} from 'components/common/AuditLog/styled';
import {useCursorAuditLog} from 'components/common/AuditLog/useAuditLog';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {DateRange} from 'components/ui-kit/DateRange';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {GridFieldConfig} from 'types';
import {CUSTOMS_AUDIT_EVENTS} from 'types/customs-audit';
import {handleNil} from 'utils/handleNil';

import {mapCustomsAuditFilters} from './filters';

export {CustomsAuditDetailPage} from './Detail';

// const NK_ROLES: UserRoles[] = [
//     UserRoles.ROLE_MPL_NK_DIRECTOR,
//     UserRoles.ROLE_MPL_NK_ADMINISTRATOR,
//     UserRoles.ROLE_MPL_NK_EMPLOYEE,
// ];

export const CustomsAuditLogPage = () => {
    const {t} = useTranslation();
    const {
        filters,
        page,
        size,
        data,
        hasNext,
        pending,
        setPage,
        changeFilters,
        resetFilters,
    } = useCursorAuditLog(fetchCustomsAuditLog, mapCustomsAuditFilters);
    // const userRoleOptions = useMemo(
    //     () =>
    //         Object.values(UserRoles)
    //             .filter((role) => !NK_ROLES.includes(role))
    //             .map((role) => ({
    //                 value: role.replace(/^ROLE_/, ''),
    //                 label: translateUserRole(t, role),
    //             })),
    //     [t],
    // );
    const fields: GridFieldConfig[] = [
        {
            id: 'eventId',
            header: t('pages.customsAudit.columns.eventId'),
            width: 310,
        },
        {
            id: 'eventType',
            header: t('pages.customsAudit.columns.eventType'),
            width: 200,
            filter: (
                <Select
                    isClearable
                    options={CUSTOMS_AUDIT_EVENTS.map((value) => ({
                        value,
                        label: t(`pages.customsAudit.events.${value}`),
                    }))}
                />
            ),
        },
        {
            id: 'eventTime',
            header: t('pages.customsAudit.columns.dateTime'),
            width: 200,
            isSortable: true,
            filter: <DateRange />,
        },
        {
            id: 'userFullName',
            header: t('pages.customsAudit.columns.userName'),
            width: 200,
            filter: <Input />,
        },
        {
            id: 'userRole',
            header: t('pages.customsAudit.columns.userRole'),
            width: 170,
            // filter: <Select isClearable options={userRoleOptions} />,
        },
        {
            id: 'branches',
            header: t('pages.customsAudit.columns.branches'),
            filter: <Input />,
        },
        {
            id: 'notificationId',
            header: t('pages.customsAudit.columns.notificationId'),
            filter: <Input />,
        },
        {
            id: 'declarationNumber',
            header: t('pages.customsAudit.columns.declarationNumber'),
            filter: <Input />,
        },
    ];
    const rows = data.map((entry) => ({
        ...entry,
        entry,
        eventId: (
            <EventId>
                <Link
                    to={generatePath(ROUTE.CUSTOMS_AUDIT_DETAIL, {id: entry.eventId})}
                    onClick={(event) => event.stopPropagation()}
                >
                    {handleNil(entry.eventId)}
                </Link>
            </EventId>
        ),
        eventType: t(`pages.customsAudit.events.${entry.eventType}`, {
            defaultValue: entry.eventType,
        }),
        eventTime: formatLocalDateTime(entry.eventTime),
        userFullName: handleNil(entry.userFullName),
        userRole: translateUserRole(t, entry.userRole),
        branches: handleNil(entry.branchesAtEventTime),
        notificationId: (
            <AuditReference
                field="notificationId"
                value={entry.notificationId}
                notificationId={entry.notificationId}
            />
        ),
        declarationNumber: (
            <AuditReference
                field="declarationNumber"
                value={entry.declarationNumber}
                notificationId={entry.notificationId}
            />
        ),
    }));

    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation>
                    <Toolbar>
                        <PermissionAccess permissions={[PERMISSION_ALIASES.customsAuditLogExport]}>
                            <AuditExportControl
                                fileName="customs-audit"
                                initialPeriod={filters.eventTime as [Date | null, Date | null]}
                                onExport={async (format, period) => {
                                    const exportParams = mapCustomsAuditFilters({
                                        ...filters,
                                        eventTime: period,
                                    });
                                    const data = await exportCustomsAuditLog(
                                        {
                                            ...exportParams,
                                            paging: {pageSize: size},
                                        },
                                        format,
                                    );
                                    return {data};
                                }}
                            />
                        </PermissionAccess>
                    </Toolbar>
                </AuditNavigation>
                <Table
                    hideSetting
                    columns={mapTableColumns(
                        fields,
                        filters,
                        changeFilters,
                        resetFilters,
                        {
                            eventTime:
                                filters.sortDesc === undefined
                                    ? undefined
                                    : filters.sortDesc === 'true',
                        },
                        (values) =>
                            changeFilters(
                                values.map(({value}) => ({
                                    id: 'sortDesc',
                                    value: value === undefined ? undefined : String(value),
                                })),
                            ),
                    )}
                    data={rows}
                    pageIndex={page}
                    hasNext={hasNext}
                    onPageChange={setPage}
                    isLoading={pending}
                />
            </PageContent>
        </Page>
    );
};
